import React, { useState, useEffect } from 'react';
import {
  User, Mail, Briefcase, Building2, Shield, Send, Clock, CheckCircle,
  XCircle, Key, AlertCircle, Trash2, Info, Users, Crown, Star,
  BarChart2, TrendingUp, Calendar, Lock, Unlock
} from 'lucide-react';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../contexts/AuthContext';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';

interface InvitationRequest {
  id: string;
  invited_first_name: string;
  invited_last_name: string;
  invited_email: string;
  invited_title: string;
  invited_department: string;
  invited_role: string;
  status: string;
  created_at: string;
  updated_at: string;
  closed_by_client_at?: string | null;
}

interface CredentialMessage {
  id: string;
  verification_code: string;
  expires_at: string;
}

interface AccountInfo {
  firstName: string;
  lastName: string;
  company: string;
  role: string;
  plan: string;
  totalSeats: number;
  email: string;
  downgradePending?: boolean;
  proGraceUntil?: string | null;
  planExpiresAt?: string | null;
}

interface TeamMemberProfile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
  status: string;
  has_pro_job_access: boolean;
  in_grace_period: boolean;
  pro_grace_until: string | null;
  invited_by: string | null;
  created_at: string;
}

export default function InviteTeamMember() {
  const { theme } = useWorkdayClientTheme();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [accountInfo, setAccountInfo] = useState<AccountInfo>({
    firstName: '', lastName: '', company: '', role: 'account_owner',
    plan: 'free', totalSeats: 0, email: '',
    downgradePending: false, proGraceUntil: null, planExpiresAt: null,
  });

  const [inviteForm, setInviteForm] = useState({
    firstName: '', lastName: '', title: '', email: '',
    department: '', requestedRole: '', grantProJobAccess: false
  });

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [requests, setRequests] = useState<InvitationRequest[]>([]);
  const [allRequests, setAllRequests] = useState<InvitationRequest[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [credentialMessages, setCredentialMessages] = useState<{ [requestId: string]: CredentialMessage }>({});
  const [deletingRequestId, setDeletingRequestId] = useState<string | null>(null);
  const [teamMembers, setTeamMembers] = useState<TeamMemberProfile[]>([]);
  const [updatingProAccess, setUpdatingProAccess] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'invite' | 'members' | 'history' | 'analytics'>('invite');

  useEffect(() => {
    if (!user) return;
    fetchAccountInfo();
    fetchUserRequests();
    fetchAllRequests();
    fetchTeamMembers();

    const requestsChannel = supabase
      .channel('invitation_requests_changes')
      .on('postgres_changes', {
        event: '*', schema: 'public', table: 'user_account_requests',
        filter: `workday_customer_id=eq.${user.id}`
      }, () => { fetchUserRequests(); fetchAllRequests(); fetchTeamMembers(); })
      .subscribe();

    return () => { requestsChannel.unsubscribe(); };
  }, [user]);

  const fetchAccountInfo = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('workday_client_profiles')
      .select('first_name, last_name, email, role, plan, total_seats, downgrade_pending, pro_grace_until, plan_expires_at, companies(name)')
      .eq('id', user.id)
      .maybeSingle();

    if (data) {
      setAccountInfo({
        firstName: data.first_name || '',
        lastName: data.last_name || '',
        email: data.email || '',
        company: (data.companies as any)?.name || '',
        role: data.role || 'account_owner',
        plan: data.plan || 'free',
        totalSeats: data.total_seats || 0,
        downgradePending: data.downgrade_pending || false,
        proGraceUntil: data.pro_grace_until || null,
        planExpiresAt: data.plan_expires_at || null,
      });
    }
  };

  const fetchUserRequests = async () => {
    if (!user) return;
    const { data, error } = await supabase
      .from('user_account_requests')
      .select('*')
      .eq('workday_customer_id', user.id)
      .not('invited_email', 'eq', '')
      .neq('status', 'closed')
      .order('created_at', { ascending: false });

    if (!error) {
      setRequests(data || []);
      const approved = (data || []).filter(r => r.status === 'approved');
      for (const req of approved) fetchCredentialMessage(req.id);
    }
    setLoadingRequests(false);
  };

  const fetchAllRequests = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('user_account_requests')
      .select('*')
      .eq('workday_customer_id', user.id)
      .not('invited_email', 'eq', '')
      .order('created_at', { ascending: false });
    setAllRequests(data || []);
  };

  const fetchTeamMembers = async () => {
    if (!user) return;
    const { data } = await supabase
      .from('workday_client_profiles')
      .select('id, first_name, last_name, email, role, status, has_pro_job_access, in_grace_period, pro_grace_until, invited_by, created_at')
      .eq('invited_by', user.id)
      .order('created_at', { ascending: false });
    setTeamMembers(data || []);
  };

  const fetchCredentialMessage = async (requestId: string) => {
    const { data } = await supabase
      .from('credential_messages')
      .select('id, verification_code, expires_at')
      .eq('user_account_request_id', requestId)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (data) {
      setCredentialMessages(prev => ({ ...prev, [requestId]: data }));
    }
  };

  const toggleProJobAccess = async (memberId: string, current: boolean) => {
    setUpdatingProAccess(memberId);
    const { error } = await supabase
      .from('workday_client_profiles')
      .update({ has_pro_job_access: !current })
      .eq('id', memberId);
    if (!error) {
      setTeamMembers(prev => prev.map(m =>
        m.id === memberId ? { ...m, has_pro_job_access: !current } : m
      ));
    }
    setUpdatingProAccess(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteForm.firstName || !inviteForm.lastName || !inviteForm.email || !inviteForm.requestedRole) {
      setMessage('Please fill in all required fields.');
      setTimeout(() => setMessage(''), 3000);
      return;
    }
    if (!user?.id) return;

    // No hard seat cap on free plan — seats are unlimited on free.
    // On Pro, totalSeats tracks purchased extras but is also not enforced as a hard cap here.
    // Admin approves each request individually.

    setSubmitting(true);
    setMessage('');

    const requestorName = `${accountInfo.firstName || 'User'} ${accountInfo.lastName || 'Account'}`.trim();
    const { error } = await supabase
      .from('user_account_requests')
      .insert({
        application_name: 'Workday Services',
        requestor_name: requestorName,
        company_name: accountInfo.company || 'Company Pending Setup',
        user_role: accountInfo.role || 'account_owner',
        request_type: 'team_member_access',
        invited_first_name: inviteForm.firstName,
        invited_last_name: inviteForm.lastName,
        invited_title: inviteForm.title || '',
        invited_email: inviteForm.email,
        invited_department: inviteForm.department || '',
        invited_role: inviteForm.requestedRole,
        workday_customer_id: user.id,
        status: 'pending',
        request_details: JSON.stringify({
          plan: accountInfo.plan,
          grant_pro_job_access: inviteForm.grantProJobAccess,
        }),
      });

    if (error) {
      setMessage(`Error: ${error.message}`);
    } else {
      setMessage('Invitation submitted! An admin will review and approve it shortly.');
      setInviteForm({ firstName: '', lastName: '', title: '', email: '', department: '', requestedRole: '', grantProJobAccess: false });
      fetchUserRequests();
      fetchAllRequests();
    }
    setSubmitting(false);
    setTimeout(() => setMessage(''), 5000);
  };

  const handleDeleteRequest = async (requestId: string) => {
    if (!confirm('Remove this invitation request?')) return;
    setDeletingRequestId(requestId);
    const { error } = await supabase
      .from('user_account_requests')
      .update({ status: 'closed', closed_by_client_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq('id', requestId);
    if (!error) {
      setMessage('Invitation request removed.');
      fetchUserRequests();
      fetchAllRequests();
      setTimeout(() => setMessage(''), 3000);
    }
    setDeletingRequestId(null);
  };

  // Seat calculations
  const approvedActive = requests.filter(r => r.status === 'approved').length;
  const pendingCount = requests.filter(r => r.status === 'pending').length;
  const seatsUsed = 1 + approvedActive; // 1 = account owner
  const isPro = accountInfo.plan === 'pro';
  // No hard cap on free — seatsRemaining only shown for Pro accounts with purchased quotas
  const purchasedTotal = accountInfo.totalSeats || 0;
  const seatsRemaining = isPro && purchasedTotal > 0 ? Math.max(0, purchasedTotal - seatsUsed) : null;
  // Is account in downgrade grace period (pro features still active until grace expiry)
  const inGrace = !isPro && !!accountInfo.proGraceUntil && new Date(accountInfo.proGraceUntil) > new Date();
  const graceExpiry = accountInfo.proGraceUntil ? new Date(accountInfo.proGraceUntil) : null;

  // Analytics
  const totalEverInvited = allRequests.length;
  const totalApproved = allRequests.filter(r => r.status === 'approved').length;
  const totalRejected = allRequests.filter(r => r.status === 'rejected').length;
  const totalClosed = allRequests.filter(r => r.status === 'closed').length;

  const roleOptions = [
    { value: 'team_member', label: 'Team Member', description: 'View dashboard, browse talent profiles, track project activity. Cannot post jobs, approve timesheets, manage contracts, or access billing.' },
    { value: 'team_member_with_billing', label: 'Team Member + Billing', description: 'All Team Member access plus invoices, subscription details, and billing history.' },
    { value: 'admin', label: 'Admin', description: 'Post jobs, approve timesheets, and manage contracts. No billing access.' },
    { value: 'admin_with_billing', label: 'Admin + Billing', description: 'Full admin capabilities plus complete billing and invoice access.' },
    { value: 'restricted', label: 'Restricted', description: 'Read-only access limited to the dashboard and basic account information.' },
  ];

  const getStatusBadge = (status: string) => {
    const cfg: Record<string, { cls: string; icon: React.ReactNode }> = {
      pending: { cls: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30', icon: <Clock className="w-3.5 h-3.5" /> },
      approved: { cls: 'bg-green-500/10 text-green-500 border-green-500/30', icon: <CheckCircle className="w-3.5 h-3.5" /> },
      rejected: { cls: 'bg-red-500/10 text-red-500 border-red-500/30', icon: <XCircle className="w-3.5 h-3.5" /> },
      closed: { cls: 'bg-gray-500/10 text-gray-400 border-gray-500/30', icon: <XCircle className="w-3.5 h-3.5" /> },
    };
    const c = cfg[status] || cfg.pending;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full border text-xs font-medium ${c.cls}`}>
        {c.icon}{status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const card = `rounded-xl border p-5 ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`;
  const label = `block text-sm font-medium mb-1.5 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`;
  const input = `w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-[#40E0D0] focus:border-transparent outline-none transition-colors ${
    theme === 'dark' ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
  }`;

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <WorkdayClientHeader />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-32 pb-16">

        {/* Page header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/workday-client/settings')}
            className={`p-2 rounded-lg transition-colors ${theme === 'dark' ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-500 hover:bg-gray-100'}`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold">Team Members</h1>
            <p className={`text-sm mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Invite and manage your team's access</p>
          </div>
        </div>

        {message && (
          <div className={`p-4 rounded-lg mb-6 text-sm font-medium ${
            message.startsWith('Error') || message.includes('all ')
              ? 'bg-red-50 text-red-700 border border-red-200'
              : 'bg-green-50 text-green-700 border border-green-200'
          }`}>
            {message}
          </div>
        )}

        {/* Seat Overview Banner */}
        <div className={`rounded-xl border-2 overflow-hidden mb-6 ${
          isPro ? 'border-[#40E0D0]' : inGrace ? 'border-amber-400' : 'border-gray-300 dark:border-gray-600'
        }`}>
          <div className={`px-5 py-4 ${
            isPro ? 'bg-gradient-to-r from-[#40E0D0]/10 to-[#0066FF]/5'
            : inGrace ? (theme === 'dark' ? 'bg-amber-900/20' : 'bg-amber-50')
            : theme === 'dark' ? 'bg-gray-800' : 'bg-white'
          }`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {isPro || inGrace
                  ? <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPro ? 'bg-[#40E0D0]/15' : 'bg-amber-100'}`}>
                      <Star className={`w-5 h-5 ${isPro ? 'text-[#40E0D0]' : 'text-amber-500'}`} />
                    </div>
                  : <div className={`w-10 h-10 rounded-full flex items-center justify-center ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <Users className="w-5 h-5 text-gray-400" />
                    </div>
                }
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-base">{accountInfo.company || 'Your Account'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold tracking-wide ${
                      isPro ? 'bg-[#40E0D0] text-black'
                      : inGrace ? 'bg-amber-400 text-black'
                      : theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {isPro ? 'PRO' : inGrace ? 'PRO (Grace)' : 'FREE'}
                    </span>
                  </div>
                  <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    {isPro ? '$499.99/mo — PerplexityATS Pro'
                    : inGrace ? `Pro access active through ${graceExpiry?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
                    : 'Free Plan — Unlimited Seats'}
                  </p>
                </div>
              </div>

              {/* Seat meters */}
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold">{seatsUsed}</div>
                  <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Active Seats</div>
                </div>
                {isPro && seatsRemaining !== null && (
                  <div className="text-center">
                    <div className={`text-2xl font-bold ${seatsRemaining === 0 ? 'text-red-500' : 'text-[#40E0D0]'}`}>{seatsRemaining}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Available</div>
                  </div>
                )}
                {isPro && purchasedTotal > 0 && (
                  <div className="text-center">
                    <div className="text-2xl font-bold">{purchasedTotal}</div>
                    <div className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Purchased</div>
                  </div>
                )}
              </div>
            </div>

            {/* Seat dots */}
            <div className="mt-4 flex items-center gap-4 flex-wrap">
              {/* Owner seat */}
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full flex-shrink-0 bg-[#40E0D0]" />
                <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Seat 1 — Account Owner (You)</span>
              </div>
              {/* Team member seats */}
              {teamMembers.map((m, i) => (
                <div key={m.id} className="flex items-center gap-1.5">
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${
                    m.has_pro_job_access || m.in_grace_period ? 'bg-[#40E0D0]' : 'bg-blue-400'
                  }`} />
                  <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Seat {i + 2} — {m.first_name} {m.last_name}
                    {m.in_grace_period && m.pro_grace_until && new Date(m.pro_grace_until) > new Date()
                      ? ` (Pro until ${new Date(m.pro_grace_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`
                      : ''}
                  </span>
                </div>
              ))}
              {pendingCount > 0 && (
                <span className={`text-xs ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'}`}>
                  {pendingCount} pending approval
                </span>
              )}
            </div>

            {/* Grace period notice */}
            {inGrace && graceExpiry && (
              <div className="mt-3 p-2.5 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5 text-amber-600" />
                <span>
                  Your account was downgraded to Free, but all seats retain full Pro access (job postings, pipeline, candidate messaging) through <strong>{graceExpiry.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.
                  After this date, all seats revert to Free features. Purchased seats remain but operate under the Free plan.
                </span>
              </div>
            )}

            {/* Free plan info note */}
            {!isPro && !inGrace && (
              <div className={`mt-3 p-2.5 rounded-lg border text-xs flex items-start gap-2 ${theme === 'dark' ? 'bg-gray-700/50 border-gray-600 text-gray-400' : 'bg-blue-50 border-blue-100 text-blue-700'}`}>
                <Info className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>
                  Free plan includes unlimited seats. Upgrade to Pro ($499.99/mo) for Pro job postings, pipeline access, and candidate messaging. Additional seats can be purchased at <strong>$99 each</strong>.
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className={`flex gap-1 p-1 rounded-xl mb-6 ${theme === 'dark' ? 'bg-gray-800' : 'bg-gray-100'}`}>
          {([
            { key: 'invite', label: 'Invite', icon: <Send className="w-4 h-4" /> },
            { key: 'members', label: 'Active Members', icon: <Users className="w-4 h-4" />, badge: teamMembers.length || undefined },
            { key: 'history', label: 'Request History', icon: <Calendar className="w-4 h-4" /> },
            { key: 'analytics', label: 'Analytics', icon: <BarChart2 className="w-4 h-4" /> },
          ] as { key: typeof activeTab; label: string; icon: React.ReactNode; badge?: number }[]).map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.key
                  ? 'bg-[#40E0D0] text-black shadow-sm'
                  : theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-800'
              }`}
            >
              {tab.icon}
              <span className="hidden sm:inline">{tab.label}</span>
              {tab.badge !== undefined && (
                <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === tab.key ? 'bg-black/20 text-black' : 'bg-[#40E0D0]/20 text-[#40E0D0]'
                }`}>{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* TAB: Invite */}
        {activeTab === 'invite' && (
          <div className={card}>
            <h2 className="text-lg font-semibold mb-5">Send Invitation Request</h2>
            <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={label}>First Name <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" value={inviteForm.firstName} onChange={e => setInviteForm(p => ({ ...p, firstName: e.target.value }))} required className={`${input} pl-9`} placeholder="Jane" />
                    </div>
                  </div>
                  <div>
                    <label className={label}>Last Name <span className="text-red-500">*</span></label>
                    <input type="text" value={inviteForm.lastName} onChange={e => setInviteForm(p => ({ ...p, lastName: e.target.value }))} required className={input} placeholder="Smith" />
                  </div>
                  <div>
                    <label className={label}>Email <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="email" value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} required className={`${input} pl-9`} placeholder="jane@company.com" />
                    </div>
                  </div>
                  <div>
                    <label className={label}>Job Title</label>
                    <div className="relative">
                      <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" value={inviteForm.title} onChange={e => setInviteForm(p => ({ ...p, title: e.target.value }))} className={`${input} pl-9`} placeholder="HR Manager" />
                    </div>
                  </div>
                  <div className="sm:col-span-2">
                    <label className={label}>Department</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input type="text" value={inviteForm.department} onChange={e => setInviteForm(p => ({ ...p, department: e.target.value }))} className={`${input} pl-9`} placeholder="Human Resources" />
                    </div>
                  </div>
                </div>

                {/* Role selector */}
                <div>
                  <label className={label}>Access Role <span className="text-red-500">*</span></label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                    {roleOptions.map(role => {
                      const selected = inviteForm.requestedRole === role.value;
                      return (
                        <button
                          key={role.value}
                          type="button"
                          onClick={() => setInviteForm(p => ({ ...p, requestedRole: role.value }))}
                          className={`group relative text-left p-3 rounded-lg border-2 transition-all ${
                            selected
                              ? 'border-[#40E0D0] bg-[#40E0D0]/10'
                              : theme === 'dark' ? 'border-gray-600 hover:border-gray-400' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <div className="flex items-center justify-between gap-1">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <Shield className={`w-3.5 h-3.5 flex-shrink-0 ${selected ? 'text-[#40E0D0]' : 'text-gray-400'}`} />
                              <span className={`text-sm font-medium truncate ${selected ? 'text-[#40E0D0]' : ''}`}>{role.label}</span>
                            </div>
                            <Info className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                          </div>
                          {selected && <CheckCircle className="w-3 h-3 text-[#40E0D0] mt-1.5" />}
                          {/* Tooltip */}
                          <div className={`absolute bottom-full left-0 mb-2 w-64 p-3 rounded-lg shadow-xl border text-xs leading-relaxed z-20 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity ${
                            theme === 'dark' ? 'bg-gray-800 border-gray-600 text-gray-200' : 'bg-gray-900 border-gray-700 text-gray-100'
                          }`}>
                            <p className="font-semibold text-[#40E0D0] mb-1">{role.label}</p>
                            {role.description}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Pro job access toggle — only for pro accounts */}
                {isPro && (
                  <div className={`flex items-center justify-between p-4 rounded-lg border-2 border-[#40E0D0]/30 ${theme === 'dark' ? 'bg-[#40E0D0]/5' : 'bg-[#40E0D0]/5'}`}>
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-[#40E0D0] flex-shrink-0" />
                      <div>
                        <p className="text-sm font-semibold">Pro Job Postings Access</p>
                        <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          Allow this team member to view and interact with Pro-tier job postings
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setInviteForm(p => ({ ...p, grantProJobAccess: !p.grantProJobAccess }))}
                      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${inviteForm.grantProJobAccess ? 'bg-[#40E0D0]' : theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'}`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${inviteForm.grantProJobAccess ? 'translate-x-5' : ''}`} />
                    </button>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitting || !inviteForm.requestedRole}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#40E0D0] text-black font-semibold rounded-lg hover:bg-[#40E0D0]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="w-4 h-4" />
                  {submitting ? 'Submitting...' : 'Send Invitation Request'}
                </button>
                <p className={`text-xs text-center ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                  Invitations are reviewed and approved by the Perplexity team before access is granted.
                </p>
              </form>
          </div>
        )}

        {/* TAB: Active Members */}
        {activeTab === 'members' && (
          <div className="space-y-4">
            {/* Owner row */}
            <div className={`${card} flex items-center gap-4`}>
              <div className="w-10 h-10 rounded-full bg-[#40E0D0]/15 flex items-center justify-center flex-shrink-0">
                <Crown className="w-5 h-5 text-[#40E0D0]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold">{accountInfo.firstName} {accountInfo.lastName}</span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#40E0D0] text-black">Account Owner</span>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#40E0D0] text-black">Seat 1</span>
                  {isPro && <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-[#40E0D0]/20 text-[#40E0D0]">Pro</span>}
                </div>
                <p className={`text-sm mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{accountInfo.email}</p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-600 font-medium">Active</span>
              </div>
            </div>

            {/* Invited members */}
            {teamMembers.length === 0 ? (
              <div className={`${card} text-center py-10`}>
                <Users className={`w-10 h-10 mx-auto mb-3 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
                <p className={`font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>No active team members yet</p>
                <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}`}>Approved invitations will appear here.</p>
                <button onClick={() => setActiveTab('invite')} className="mt-4 text-[#40E0D0] text-sm hover:underline">Send an invitation</button>
              </div>
            ) : (
              teamMembers.map((member, idx) => (
                <div key={member.id} className={`${card} flex items-center gap-4`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <User className="w-5 h-5 text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{member.first_name} {member.last_name}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${theme === 'dark' ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-50 text-blue-700'}`}>
                        Seat {idx + 2}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-xs ${theme === 'dark' ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                        {member.role.replace(/_/g, ' ')}
                      </span>
                    </div>
                    <p className={`text-sm mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{member.email}</p>
                  </div>
                  {/* Pro job access toggle — shown for Pro accounts OR grace period seats */}
                  {(isPro || member.in_grace_period) && (
                    <div className="flex flex-col items-center gap-1 flex-shrink-0">
                      <span className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                        {member.in_grace_period ? 'Grace' : 'Pro Jobs'}
                      </span>
                      {isPro ? (
                        <button
                          onClick={() => toggleProJobAccess(member.id, member.has_pro_job_access)}
                          disabled={updatingProAccess === member.id}
                          className={`relative w-10 h-5 rounded-full transition-colors ${member.has_pro_job_access ? 'bg-[#40E0D0]' : theme === 'dark' ? 'bg-gray-600' : 'bg-gray-300'} disabled:opacity-50`}
                          title={member.has_pro_job_access ? 'Revoke Pro job access' : 'Grant Pro job access'}
                        >
                          <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${member.has_pro_job_access ? 'translate-x-5' : ''}`} />
                        </button>
                      ) : (
                        /* Grace period — read-only indicator */
                        <div className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">
                          Until {member.pro_grace_until ? new Date(member.pro_grace_until).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                        </div>
                      )}
                    </div>
                  )}
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {member.has_pro_job_access || (member.in_grace_period && member.pro_grace_until && new Date(member.pro_grace_until) > new Date())
                      ? <Unlock className={`w-4 h-4 ${member.in_grace_period ? 'text-amber-500' : 'text-[#40E0D0]'}`} />
                      : <Lock className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'}`} />
                    }
                    <span className={`text-xs ${member.status === 'active' ? 'text-green-500' : 'text-gray-400'}`}>
                      {member.status}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* TAB: Request History */}
        {activeTab === 'history' && (
          <div className="space-y-4">
            {/* Active requests */}
            <div className={card}>
              <h3 className="font-semibold mb-4">Active Requests</h3>
              {loadingRequests ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#40E0D0]" />
                </div>
              ) : requests.length === 0 ? (
                <p className={`text-sm text-center py-6 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>No active requests.</p>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {requests.map(req => (
                    <div key={req.id} className="py-4 first:pt-0 last:pb-0">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="font-medium">{req.invited_first_name} {req.invited_last_name}</span>
                            {getStatusBadge(req.status)}
                          </div>
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{req.invited_email}</p>
                          <div className={`flex flex-wrap gap-3 mt-1 text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                            {req.invited_role && <span>Role: {req.invited_role.replace(/_/g, ' ')}</span>}
                            {req.invited_department && <span>Dept: {req.invited_department}</span>}
                            <span>Submitted: {new Date(req.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDeleteRequest(req.id)}
                          disabled={deletingRequestId === req.id}
                          className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ${theme === 'dark' ? 'text-red-400 hover:bg-red-900/30' : 'text-red-500 hover:bg-red-50'}`}
                          title="Cancel request"
                        >
                          {deletingRequestId === req.id
                            ? <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                            : <Trash2 className="w-4 h-4" />
                          }
                        </button>
                      </div>
                      {req.status === 'approved' && credentialMessages[req.id] && (
                        <div className="mt-3 p-3 rounded-lg bg-green-50 border border-green-200 flex items-start gap-3">
                          <Key className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs font-semibold text-green-900">Credentials sent — Code: {credentialMessages[req.id].verification_code}</p>
                            <p className="text-xs text-green-700 mt-0.5">Check your Messages from Perplexity Team for login credentials.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Full history */}
            <div className={card}>
              <h3 className="font-semibold mb-4">Full Request History</h3>
              {allRequests.length === 0 ? (
                <p className={`text-sm text-center py-6 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>No invitation history yet.</p>
              ) : (
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {allRequests.map(req => (
                    <div key={req.id} className="py-3 first:pt-0 last:pb-0 flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                        <User className="w-4 h-4 text-gray-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium">{req.invited_first_name} {req.invited_last_name}</span>
                          {getStatusBadge(req.status)}
                        </div>
                        <p className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                          {req.invited_email} · {req.invited_role?.replace(/_/g, ' ')} · {new Date(req.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: Analytics */}
        {activeTab === 'analytics' && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {[
                { label: 'Total Invitations', value: totalEverInvited, icon: <Users className="w-5 h-5" />, color: 'text-[#40E0D0]' },
                { label: 'Approved', value: totalApproved, icon: <CheckCircle className="w-5 h-5" />, color: 'text-green-500' },
                { label: 'Rejected', value: totalRejected, icon: <XCircle className="w-5 h-5" />, color: 'text-red-500' },
                { label: 'Cancelled', value: totalClosed, icon: <Clock className="w-5 h-5" />, color: 'text-gray-400' },
              ].map(s => (
                <div key={s.label} className={card}>
                  <div className={`${s.color} mb-2`}>{s.icon}</div>
                  <div className="text-2xl font-bold">{s.value}</div>
                  <div className={`text-xs mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Seat utilization */}
            <div className={card}>
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#40E0D0]" />
                Seat Utilization
              </h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>Account Owner (Seat 1)</span>
                  <span className="text-[#40E0D0] font-medium">Active</span>
                </div>
                {teamMembers.map((member, i) => (
                  <div key={member.id} className="flex items-center justify-between text-sm">
                    <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                      Seat {i + 2} — {member.first_name} {member.last_name}
                    </span>
                    <div className="flex items-center gap-2">
                      {member.in_grace_period && member.pro_grace_until && new Date(member.pro_grace_until) > new Date() && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-700">Grace</span>
                      )}
                      {member.has_pro_job_access && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-[#40E0D0]/20 text-[#40E0D0]">Pro</span>
                      )}
                      <span className="text-blue-500 font-medium text-xs">Active</span>
                    </div>
                  </div>
                ))}
                {teamMembers.length === 0 && (
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>No team members yet.</p>
                )}
              </div>
              <div className={`mt-4 pt-4 border-t ${theme === 'dark' ? 'border-gray-700' : 'border-gray-100'}`}>
                <div className="flex justify-between text-sm font-medium">
                  <span>Total Active Seats</span>
                  <span className="text-[#40E0D0]">{seatsUsed}</span>
                </div>
                {isPro && purchasedTotal > 0 && (
                  <div className="flex justify-between text-sm mt-2">
                    <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>Purchased Seat Quota</span>
                    <span>{seatsUsed} / {purchasedTotal} used</span>
                  </div>
                )}
              </div>
            </div>

            {/* Plan info */}
            <div className={card}>
              <h3 className="font-semibold mb-4 flex items-center gap-2">
                <Star className="w-4 h-4 text-[#40E0D0]" />
                Plan Details
              </h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className={`text-xs block mb-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Current Plan</span>
                  <span className="font-semibold">
                    {isPro ? 'PerplexityATS Pro' : inGrace ? 'Free (Grace Period)' : 'Free'}
                  </span>
                </div>
                {isPro && (
                  <div>
                    <span className={`text-xs block mb-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Monthly Rate</span>
                    <span className="font-semibold">$499.99/mo</span>
                  </div>
                )}
                <div>
                  <span className={`text-xs block mb-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Seat Cap</span>
                  <span className="font-semibold">{isPro && purchasedTotal > 0 ? purchasedTotal : 'Unlimited'}</span>
                </div>
                <div>
                  <span className={`text-xs block mb-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Active Seats</span>
                  <span className="font-semibold text-[#40E0D0]">{seatsUsed}</span>
                </div>
                {(isPro || inGrace) && (
                  <div className="col-span-2">
                    <span className={`text-xs block mb-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Members with Pro Job Access</span>
                    <span className="font-semibold">{teamMembers.filter(m => m.has_pro_job_access || (m.in_grace_period && m.pro_grace_until && new Date(m.pro_grace_until) > new Date())).length} of {teamMembers.length}</span>
                  </div>
                )}
                {inGrace && graceExpiry && (
                  <div className="col-span-2">
                    <span className={`text-xs block mb-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Pro Grace Expires</span>
                    <span className="font-semibold text-amber-600">{graceExpiry.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                )}
              </div>
              {!isPro && !inGrace && (
                <div className={`mt-4 p-3 rounded-lg border border-dashed text-center ${theme === 'dark' ? 'border-gray-600' : 'border-gray-300'}`}>
                  <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                    Upgrade to PerplexityATS Pro for pro job postings, pipeline access, and candidate messaging. Additional seats at $99 each.
                  </p>
                </div>
              )}
            </div>

            {/* Invitation timeline */}
            {allRequests.length > 0 && (
              <div className={card}>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-[#40E0D0]" />
                  Invitation Timeline
                </h3>
                <div className="relative">
                  <div className={`absolute left-3.5 top-0 bottom-0 w-px ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'}`} />
                  <div className="space-y-4">
                    {allRequests.slice(0, 10).map(req => (
                      <div key={req.id} className="relative pl-9">
                        <div className={`absolute left-1.5 top-1.5 w-4 h-4 rounded-full border-2 ${
                          req.status === 'approved' ? 'border-green-500 bg-green-500' :
                          req.status === 'rejected' ? 'border-red-500 bg-red-500' :
                          req.status === 'closed' ? 'border-gray-400 bg-gray-400' :
                          'border-yellow-500 bg-yellow-500'
                        }`} />
                        <p className="text-sm font-medium">{req.invited_first_name} {req.invited_last_name}</p>
                        <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                          {req.invited_email} · {new Date(req.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </p>
                        <div className="mt-0.5">{getStatusBadge(req.status)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
